import '@/vendor/chatwoot-sdk.js'

export type ChatwootInit = {
  baseUrl: string
  websiteToken: string
}

/** Nginx same-origin proxy to local Chatwoot (includes WebSocket /cable). */
export const CHATWOOT_BASE_PATH = '/cw'

/** Cloud2Voice BYOC — Chatwoot fully disabled on their panels / domains. */
export const CLOUD2VOICE_RESELLER_ID = 'dfbc1a7a-51a7-4623-8623-1cde23c53512'

const CLOUD2VOICE_HOST_MATCHERS = ['tellimon.com'] as const

export function isCloud2VoiceHostname(hostname?: string): boolean {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
    .trim()
    .toLowerCase()
  if (!host) return false
  return CLOUD2VOICE_HOST_MATCHERS.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  )
}

/** True when Chatwoot must not load (Cloud2Voice host and/or reseller session). */
export function isChatwootDisabledForPanel(opts?: {
  resellerId?: string | null
  hostname?: string
}): boolean {
  if (isCloud2VoiceHostname(opts?.hostname)) return true
  const resellerId = (opts?.resellerId ?? '').trim()
  return resellerId === CLOUD2VOICE_RESELLER_ID
}

declare global {
  interface Window {
    chatwootSDK?: { run: (opts: { websiteToken: string; baseUrl: string }) => void }
    $chatwoot?: {
      hasLoaded?: boolean
      setUser?: (identifier: string, attrs?: Record<string, unknown>) => void
      setCustomAttributes?: (attrs: Record<string, unknown>) => void
      toggle?: (state?: 'open' | 'close') => void
    }
  }
}

function normalizeBaseUrl(u: string): string {
  const t = (u || '').trim()
  if (!t) return ''
  return t.endsWith('/') ? t.slice(0, -1) : t
}

export function chatwootConfigFromEnv(): ChatwootInit | null {
  const websiteToken = (import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || '').trim()
  const fromEnv = normalizeBaseUrl(import.meta.env.VITE_CHATWOOT_BASE_URL || '')
  const baseUrl =
    fromEnv ||
    (typeof window !== 'undefined' ? `${window.location.origin}${CHATWOOT_BASE_PATH}` : '')
  if (!baseUrl || !websiteToken) return null
  return { baseUrl, websiteToken }
}

export function waitForChatwootReady(timeoutMs = 20000): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.$chatwoot?.hasLoaded) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('Chatwoot widget ready timed out')),
      timeoutMs
    )
    window.addEventListener(
      'chatwoot:ready',
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      { once: true }
    )
  })
}

export async function ensureChatwootLoaded(): Promise<void> {
  if (typeof window === 'undefined') return
  if (!window.chatwootSDK) {
    throw new Error('Chatwoot SDK unavailable')
  }
}

/** Start the widget launcher (bubble appears when the iframe finishes loading). */
export async function initChatwoot(opts: ChatwootInit): Promise<void> {
  if (typeof window === 'undefined') return
  await ensureChatwootLoaded()
  await waitForChatwootServiceWorker()
  if (!window.$chatwoot?.hasLoaded) {
    window.chatwootSDK!.run({ websiteToken: opts.websiteToken, baseUrl: opts.baseUrl })
  }
}

export async function registerChatwootServiceWorker(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    await navigator.serviceWorker.register('/sw-chatwoot.js', { scope: '/' })
  } catch {
    // Non-fatal — widget may still work if Cloudflare allows the config POST.
  }
}

/** Wait until the Chatwoot service worker controls fetch (config POST rewrite). */
export async function waitForChatwootServiceWorker(timeoutMs = 8000): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return
  await registerChatwootServiceWorker()
  if (navigator.serviceWorker.controller) return

  await Promise.race([
    new Promise<void>((resolve) => {
      const onController = () => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.removeEventListener('controllerchange', onController)
          resolve()
        }
      }
      navigator.serviceWorker.addEventListener('controllerchange', onController)
      navigator.serviceWorker.ready.then(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.removeEventListener('controllerchange', onController)
          resolve()
        }
      })
    }),
    new Promise<void>((_, reject) => {
      window.setTimeout(() => reject(new Error('Chatwoot service worker not ready')), timeoutMs)
    }),
  ]).catch(() => {
    // First visit may not control until reload; nginx also proxies config POST.
  })
}
